import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toTitleCase } from "@/lib/utils";
import { ArrowLeft, Microscope, Hash, Barcode, Layers } from "lucide-react";

export default function MyInstrumentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const items = useMemo(() => (user?.instruments || []).filter(Boolean), [user]);

  return (
    <div className="space-y-4" data-testid="my-instruments-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="my-instruments-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">My Instruments</h1>
          <p className="text-xs text-gray-500">{items.length} instrument{items.length === 1 ? "" : "s"} mapped to your registry</p>
        </div>
      </div>

      {!user?.registry_id && (
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="no-registry-msg">
          No Registry ID linked to your account. Please contact admin to map your instruments.
        </div>
      )}

      {items.length === 0 ? (
        <div className="py-16 text-center" data-testid="empty-instruments">
          <div className="w-16 h-16 mx-auto rounded-full bg-gray-50 flex items-center justify-center mb-3">
            <Microscope className="w-7 h-7 text-gray-300" />
          </div>
          <p className="text-sm font-semibold text-gray-700">No instruments yet</p>
          <p className="text-xs text-gray-400 mt-1">Once an instrument is mapped to your Registry ID it will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="instruments-list">
          {items.map((it, idx) => (
            <div key={`${it.SERIAL_NUMBER || it.ITEM_CODE}-${idx}`} className="bg-white rounded-2xl border border-slate-200 p-4 hover:border-[#008A99]/40 hover:shadow-sm transition-all" data-testid={`instrument-card-${it.ITEM_CODE || idx}`}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#E0F4F5] flex items-center justify-center flex-shrink-0">
                  <Microscope className="w-5 h-5 text-[#008A99]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-900 truncate">{toTitleCase(it.PRODUCT_SHORT_NAME || it.PRODUCT_SUB_CATEGORY || it.ITEM_CODE || "Instrument")}</p>
                  {it.PRODUCT_SUB_CATEGORY && it.PRODUCT_SUB_CATEGORY !== it.PRODUCT_SHORT_NAME && (
                    <p className="text-[11px] text-gray-500 mt-0.5 truncate">{toTitleCase(it.PRODUCT_SUB_CATEGORY)}</p>
                  )}
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-2 text-[11px]">
                    {it.ITEM_CODE && (
                      <span className="flex items-center gap-1 text-gray-500"><Barcode className="w-3 h-3" /><span className="font-mono truncate">{it.ITEM_CODE}</span></span>
                    )}
                    {it.SERIAL_NUMBER && (
                      <span className="flex items-center gap-1 text-gray-500"><Hash className="w-3 h-3" /><span className="font-mono truncate">{it.SERIAL_NUMBER}</span></span>
                    )}
                    {it.PRODUCT_CATEGORY && (
                      <span className="flex items-center gap-1 text-gray-500 col-span-2"><Layers className="w-3 h-3" /><span className="truncate">{toTitleCase(it.PRODUCT_CATEGORY)}</span></span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
